import { useEffect, useRef, useState } from 'react';

/**
 * Browser MediaRecorder hook. Returns {start, stop, recording, blob, durationSec, error}.
 * Pick a mimeType the browser supports — Safari accepts mp4/aac, Chrome+Firefox accept webm/opus.
 */
export function useVoiceRecorder() {
  const [recording, setRecording] = useState(false);
  const [blob, setBlob] = useState(null);
  const [durationSec, setDurationSec] = useState(0);
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef(null);

  function pickMime() {
    const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
    if (typeof MediaRecorder === 'undefined') return null;
    return types.find(t => MediaRecorder.isTypeSupported(t)) || '';
  }

  async function start() {
    setError(null); setBlob(null); setDurationSec(0);
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        const insecure = typeof window !== 'undefined'
          && window.location.protocol !== 'https:'
          && window.location.hostname !== 'localhost'
          && window.location.hostname !== '127.0.0.1';
        throw new Error(insecure
          ? 'Microphone requires HTTPS. Open this site at https://… and try again.'
          : 'Your browser does not support audio recording. Use Safari 16+ or Chrome.');
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const out = new Blob(chunksRef.current, { type: rec.mimeType });
        setBlob(out);
        stream.getTracks().forEach(t => t.stop());
      };
      rec.start();
      recRef.current = rec;
      startedAtRef.current = Date.now();
      tickRef.current = setInterval(() => setDurationSec(Math.round((Date.now() - startedAtRef.current) / 1000)), 250);
      setRecording(true);
    } catch (err) {
      setError(err.message || 'Microphone access denied');
    }
  }

  function stop() {
    if (recRef.current && recRef.current.state !== 'inactive') {
      recRef.current.stop();
    }
    setRecording(false);
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  }

  function reset() {
    setBlob(null); setDurationSec(0); setError(null);
  }

  useEffect(() => () => { if (tickRef.current) clearInterval(tickRef.current); }, []);

  return { recording, blob, durationSec, error, start, stop, reset };
}
